import {
  adminLeadAssigneeDirectoryPageSizes,
  leadAssignableRoles,
  type AdminLeadAssignee,
  type AdminLeadAssigneeDirectoryPage,
  type AdminLeadAssignmentOutcome,
  type AdminLeadAssignmentRequest,
  type AdminLeadListItem,
  type LeadAssignableRole,
} from "@whitehaven/contracts";
import { isSafeLeadIdPathSegment } from "./lead-detail-route-model";
import { isBoundedOpaqueCrmCursor } from "./opaque-cursor-model";

export const MAX_LEAD_ASSIGNMENT_REVISION = 2_147_483_647;
export const UNASSIGNED_LEAD_VALUE = "" as const;

export const leadAssignableRoleLabels = {
  ADMIN: "Administrator",
  CRM_AGENT: "CRM agent",
} as const satisfies Readonly<Record<LeadAssignableRole, string>>;

const leadAssignableRoleSet: ReadonlySet<string> = new Set(leadAssignableRoles);

export interface AdminLeadAssignmentProjection {
  readonly leadId: AdminLeadListItem["leadId"];
  readonly assignedUserId: string | null;
  readonly assignedTo: AdminLeadAssignee | null;
  readonly assignmentRevision: number;
  readonly updatedAt: string;
}

export function isCanonicalLeadAssignmentTimestamp(
  value: unknown,
): value is string {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }

  const timestamp = new Date(value);
  return (
    Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value
  );
}

export function isSafeLeadAssigneeUserId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= 128 &&
    !/[^A-Za-z0-9_-]/.test(value)
  );
}

export function isAdminLeadAssignee(
  value: unknown,
): value is AdminLeadAssignee {
  if (typeof value !== "object" || value === null) return false;
  const assignee = value as AdminLeadAssignee;
  return (
    isSafeLeadAssigneeUserId(assignee.userId) &&
    typeof assignee.displayLabel === "string" &&
    assignee.displayLabel.length >= 1 &&
    assignee.displayLabel.length <= 200 &&
    assignee.displayLabel === assignee.displayLabel.trim() &&
    assignee.displayLabel === assignee.displayLabel.normalize("NFC") &&
    !/[\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}\p{Zl}\p{Zp}]/u.test(
      assignee.displayLabel,
    ) &&
    !assignee.displayLabel.includes("@") &&
    assignee.displayLabel.toLowerCase() !== assignee.userId.toLowerCase() &&
    typeof assignee.role === "string" &&
    leadAssignableRoleSet.has(assignee.role)
  );
}

export function isAdminLeadAssignmentRevision(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_LEAD_ASSIGNMENT_REVISION
  );
}

export function isAdminLeadAssignmentSnapshotConsistent(value: {
  readonly assignedUserId: unknown;
  readonly assignedTo: unknown;
  readonly assignmentRevision: unknown;
}): boolean {
  return (
    isAdminLeadAssignmentRevision(value.assignmentRevision) &&
    (value.assignedUserId === null ||
      isSafeLeadAssigneeUserId(value.assignedUserId)) &&
    (value.assignedTo === null || isAdminLeadAssignee(value.assignedTo)) &&
    (value.assignedTo === null ||
      value.assignedTo.userId === value.assignedUserId) &&
    (value.assignedUserId === null
      ? value.assignedTo === null
      : value.assignmentRevision > 0) &&
    (value.assignmentRevision !== 0 || value.assignedUserId === null)
  );
}

export function isAdminLeadAssigneeDirectoryPageConsistent(
  value: unknown,
): value is AdminLeadAssigneeDirectoryPage {
  if (typeof value !== "object" || value === null) return false;
  const page = value as AdminLeadAssigneeDirectoryPage;
  if (
    !Array.isArray(page.items) ||
    page.items.length > adminLeadAssigneeDirectoryPageSizes[0] ||
    (page.items.length === 0 && page.nextCursor !== null) ||
    (page.nextCursor !== null && !isBoundedOpaqueCrmCursor(page.nextCursor))
  ) {
    return false;
  }

  const userIds = new Set<string>();
  const presentationKeys = new Set<string>();
  return page.items.every((item) => {
    if (!isAdminLeadAssignee(item) || userIds.has(item.userId)) return false;
    const presentationKey = `${item.displayLabel
      .normalize("NFKC")
      .toLocaleLowerCase("en-US")}\u0000${item.role}`;
    if (presentationKeys.has(presentationKey)) return false;
    userIds.add(item.userId);
    presentationKeys.add(presentationKey);
    return true;
  });
}

export function mergeAdminLeadAssignees(
  current: readonly AdminLeadAssignee[],
  incoming: readonly AdminLeadAssignee[],
): readonly AdminLeadAssignee[] | null {
  const assignees = new Map(
    current.map((assignee) => [assignee.userId, assignee] as const),
  );
  const presentationKeys = new Set(
    current.map(
      (assignee) =>
        `${assignee.displayLabel
          .normalize("NFKC")
          .toLocaleLowerCase("en-US")}\u0000${assignee.role}`,
    ),
  );

  for (const assignee of incoming) {
    const presentationKey = `${assignee.displayLabel
      .normalize("NFKC")
      .toLocaleLowerCase("en-US")}\u0000${assignee.role}`;
    if (
      assignees.has(assignee.userId) ||
      presentationKeys.has(presentationKey)
    ) {
      return null;
    }
    assignees.set(assignee.userId, assignee);
    presentationKeys.add(presentationKey);
  }

  return [...assignees.values()].sort(
    (left, right) =>
      left.displayLabel.localeCompare(right.displayLabel, "en", {
        sensitivity: "base",
      }) || left.userId.localeCompare(right.userId, "en"),
  );
}

function assigneesMatch(
  left: AdminLeadAssignee | null,
  right: AdminLeadAssignee | null,
): boolean {
  return (
    left === right ||
    (left !== null &&
      right !== null &&
      left.userId === right.userId &&
      left.displayLabel === right.displayLabel &&
      left.role === right.role)
  );
}

export function isConfirmedAdminLeadAssignmentConsistent(
  current: AdminLeadAssignmentProjection,
  target: AdminLeadAssignee | null,
  request: AdminLeadAssignmentRequest,
  value: unknown,
): value is Extract<AdminLeadAssignmentOutcome, { kind: "confirmed" }> {
  if (
    typeof value !== "object" ||
    value === null ||
    !isSafeLeadIdPathSegment(current.leadId) ||
    !isAdminLeadAssignmentSnapshotConsistent(current) ||
    !isCanonicalLeadAssignmentTimestamp(current.updatedAt) ||
    !isSafeLeadIdPathSegment(request.leadId) ||
    !isAdminLeadAssignmentRevision(request.expectedAssignmentRevision) ||
    !isCanonicalLeadAssignmentTimestamp(request.expectedUpdatedAt) ||
    (request.expectedAssigneeUserId !== null &&
      !isSafeLeadAssigneeUserId(request.expectedAssigneeUserId)) ||
    (request.targetAssigneeUserId !== null &&
      !isSafeLeadAssigneeUserId(request.targetAssigneeUserId)) ||
    request.expectedAssigneeUserId === request.targetAssigneeUserId ||
    (target === null
      ? request.targetAssigneeUserId !== null
      : !isAdminLeadAssignee(target) ||
        target.userId !== request.targetAssigneeUserId)
  ) {
    return false;
  }

  const outcome = value as AdminLeadAssignmentOutcome;
  if (outcome.kind !== "confirmed") return false;

  const nextRevision = request.expectedAssignmentRevision + 1;
  const { state, transition } = outcome;
  return (
    current.leadId === request.leadId &&
    current.assignedUserId === request.expectedAssigneeUserId &&
    current.assignmentRevision === request.expectedAssignmentRevision &&
    current.updatedAt === request.expectedUpdatedAt &&
    nextRevision <= MAX_LEAD_ASSIGNMENT_REVISION &&
    typeof state === "object" &&
    state !== null &&
    state.leadId === request.leadId &&
    isAdminLeadAssignmentSnapshotConsistent(state) &&
    state.assignedUserId === request.targetAssigneeUserId &&
    assigneesMatch(state.assignedTo, target) &&
    state.assignmentRevision === nextRevision &&
    isCanonicalLeadAssignmentTimestamp(state.updatedAt) &&
    state.updatedAt > request.expectedUpdatedAt &&
    typeof transition === "object" &&
    transition !== null &&
    transition.fromAssigneeUserId === request.expectedAssigneeUserId &&
    transition.toAssigneeUserId === request.targetAssigneeUserId &&
    transition.assignmentRevision === nextRevision &&
    isCanonicalLeadAssignmentTimestamp(transition.occurredAt) &&
    transition.occurredAt === state.updatedAt
  );
}

export function applyConfirmedAdminLeadAssignment(
  current: AdminLeadListItem,
  target: AdminLeadAssignee | null,
  request: AdminLeadAssignmentRequest,
  outcome: AdminLeadAssignmentOutcome,
): AdminLeadListItem | null {
  if (
    !isConfirmedAdminLeadAssignmentConsistent(current, target, request, outcome)
  ) {
    return null;
  }

  return {
    ...current,
    assignedUserId: outcome.state.assignedUserId,
    assignedTo: outcome.state.assignedTo,
    assignmentRevision: outcome.state.assignmentRevision,
    updatedAt: outcome.state.updatedAt,
  };
}

export function isAdminLeadAssignmentFailureOutcome(
  value: unknown,
): value is Extract<
  AdminLeadAssignmentOutcome,
  { kind: "conflict" | "rejected" }
> {
  return (
    typeof value === "object" &&
    value !== null &&
    ((value as AdminLeadAssignmentOutcome).kind === "conflict" ||
      (value as AdminLeadAssignmentOutcome).kind === "rejected")
  );
}
